import { WidgetType } from './widget-types.enum';

/**
 * Widget Mapping Configuration
 *
 * Central source of truth for mapping API widget names to dashboard widget types.
 * When the my-widgets API returns a widget that doesn't plot, add an entry here.
 *
 * HOW TO ADD A NEW MAPPING:
 * 1. Get the API widgetName (e.g. "workload-and-resource-capacity")
 * 2. Normalize it: toLowerCase, remove hyphens/underscores/spaces → "workloadandresourcecapacity"
 * 3. Add entry: [normalized]: { type: WidgetType.X, config?: {...} }
 * 4. If the widget needs special config (e.g. useDepartmentData), add it in config
 *
 * RESOLUTION ORDER:
 * 1. componentKey from API (if present) - use directly as type
 * 2. WIDGET_API_MAPPING[normalized] - explicit API name variations
 * 3. WidgetType enum - exact match when API name equals enum value (normalized)
 */

/** Normalize string for matching: lowercase, remove hyphens, underscores, spaces */
export function normalizeWidgetName(value: string): string {
  return String(value || '').toLowerCase().replace(/[-_\s]/g, '');
}

export interface WidgetMappingEntry {
  type: string;
  config?: Record<string, unknown>;
}

/**
 * API widget name (normalized) → WidgetType + optional config.
 * Add new API name variations here when backend returns different formats.
 */
export const WIDGET_API_MAPPING: Record<string, WidgetMappingEntry> = {
  // Summary widgets
  departmentheadsummary: { type: WidgetType.DEPARTMENT_HEAD_SUMMARY },
  // Department head summary aggregates 3 APIs; backend may return these as separate widgets – deduplicated in single-dashboard
  intlactiveprojectssummary: { type: WidgetType.DEPARTMENT_HEAD_SUMMARY },
  teamcapacitysummary: { type: WidgetType.DEPARTMENT_HEAD_SUMMARY },
  billableutilizationsummary: { type: WidgetType.DEPARTMENT_HEAD_SUMMARY },
  teamsummary: { type: WidgetType.TASK_SUMMARY, config: { useTeamSummary: true } },
  summarycanam: { type: WidgetType.CAN_AM_SUMMARY },
  intlmgmtsummary: { type: WidgetType.INTL_MANAGEMENT_SUMMARY },

  // Feed / interaction
  interactionfeed: { type: WidgetType.INTERACTION_MENTION_FEED },

  // Leaderboard
  leaderboard: { type: WidgetType.TEAM_LEADERBOARD },

  // Calendar
  projectdeliverycalender: { type: WidgetType.DELIVERY_CALENDAR },

  // Team workload
  teamworkloadnextfourweeks: { type: WidgetType.TEAM_WORKLOAD, config: { apiMode: 'department-head' } },

  // Billable utilization
  teambillableutilization: { type: WidgetType.BILLABLE_UTILIZATION_INTL },
  departmentbillableutilization: { type: WidgetType.BILLABLE_UTILIZATION, config: { useDepartmentData: true } },

  // Workload & resource capacity
  workloadandresourcecapacity: { type: WidgetType.WORKLOAD_RESOURCE_CAPACITY_INTL },

  // Project completion / delayed
  getprojectdelayedoverview: { type: WidgetType.PROJECT_COMPLETION_DATE },

  // Client summaries
  newclientsummary: { type: WidgetType.NEW_CLIENTS_SUMMARY },
  lostclientsummary: { type: WidgetType.LOST_CLIENTS_SUMMARY },

  // Profitability
  setupprofitabilitysummary: { type: WidgetType.PROFITABILITY_SUMMARY_RECURRING, config: { type: 'setup' } },
  recurringprofitabilitysummary: { type: WidgetType.PROFITABILITY_SUMMARY_RECURRING, config: { type: 'recurring' } },
};

/** Build normalized name → WidgetType map (cached per widgetTypes to avoid rebuilding per widget) */
export function buildNormalizedToTypeMap(widgetTypes: string[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const wt of widgetTypes) {
    const norm = normalizeWidgetName(wt);
    if (!map.has(norm)) map.set(norm, wt);
  }
  return map;
}

/**
 * Resolve API widget name to WidgetType + config.
 * Returns null if no match (widget will be skipped).
 * Pass normalizedToTypeMap when resolving many widgets to avoid rebuilding the map per widget.
 */
export function resolveApiNameToWidget(
  apiName: string,
  widgetTypes: string[],
  componentKeyFromApi?: string | null,
  normalizedToTypeMap?: Map<string, string>
): WidgetMappingEntry | null {
  if (!apiName?.trim()) return null;

  // 1. If API provides componentKey that matches our WidgetType, use it directly
  if (componentKeyFromApi && widgetTypes.includes(componentKeyFromApi)) {
    return { type: componentKeyFromApi };
  }

  const normalized = normalizeWidgetName(apiName);

  // 2. Explicit mapping (API name variations)
  const mapped = WIDGET_API_MAPPING[normalized];
  if (mapped) return mapped;

  // 3. Direct match: API name equals a WidgetType enum value (normalized)
  const normalizedToType = normalizedToTypeMap ?? buildNormalizedToTypeMap(widgetTypes);
  const matchedType = normalizedToType.get(normalized) ?? (widgetTypes.includes(apiName) ? apiName : null);
  if (matchedType) return { type: matchedType };

  return null;
}

/**
 * Icon for each widget type (used in My Widgets slider).
 * Add icon when adding new widget type. Fallback: 'widgets' (Material icon).
 */
export const WIDGET_TYPE_ICONS: Record<string, string> = {
  [WidgetType.TASK_SUMMARY]: 'summarize',
  [WidgetType.MY_TASKS]: 'wl_icon_task',
  [WidgetType.MY_INTERACTION_FEED]: 'wl_icon_updates',
  [WidgetType.INTERACTION_MENTION_FEED]: 'wl_icon_updates',
  [WidgetType.UPCOMING_DEADLINES]: 'event',
  [WidgetType.UPCOMING_DEADLINES_TL]: 'event',
  [WidgetType.MY_TIMESHEET_SUMMARY]: 'schedule',
  [WidgetType.APPROVALS]: 'fa-regular fa-circle-check',
  [WidgetType.TEAM_OVERVIEW]: 'wl_icon_people',
  [WidgetType.TEAM_LEADERBOARD]: 'fa-solid fa-anchor',
  [WidgetType.TEAM_WORKLOAD]: 'timeline',
  [WidgetType.BILLABLE_UTILIZATION]: 'trending_up',
  [WidgetType.BILLABLE_UTILIZATION_INTL]: 'query_stats',
  [WidgetType.TIMESHEET_FOR_APPROVAL]: 'pending_actions',
  [WidgetType.DELIVERY_TIMELINE]: 'timeline',
  [WidgetType.TEAM_ATTENDANCE]: 'wl_icon_type',
  [WidgetType.QA_ISSUE_LIST]: 'wl_icon_issues',
  [WidgetType.MY_REQUESTS]: 'wl_icon_task',
  [WidgetType.WEEKLY_PROJECT_OVERVIEW]: 'fa-regular fa-rectangle-list',
  [WidgetType.DELIVERY_CALENDAR]: 'event_note',
  [WidgetType.PROJECTS_OVERVIEW]: 'fa-regular fa-clipboard',
  [WidgetType.DELAYED_PROJECTS_OVERVIEW]: 'wl_icon_hour',
  [WidgetType.PROJECT_COMPLETION_DATE]: 'event_available',
  [WidgetType.PROJECTS_STATUS_CHART]: 'pie_chart',
  [WidgetType.PROJECT_BREAKDOWN_CATEGORY]: 'category',
  [WidgetType.WORKLOAD_RESOURCE_CAPACITY_INTL]: 'fa-solid fa-chart-gantt',
  [WidgetType.PROFITABILITY_SUMMARY_RECURRING]: 'trending_up',
  [WidgetType.NEW_CLIENTS_SUMMARY]: 'fa-regular fa-square-plus',
  [WidgetType.LOST_CLIENTS_SUMMARY]: 'fa-regular fa-square-minus',
  [WidgetType.AI_SAVED_HOURS_MONTHLY]: 'wl_icon_ai-hour',
  [WidgetType.MEETING_GOAL_OVERVIEW]: 'event',
  [WidgetType.LEAD_PERFORMANCE_OVERVIEW]: 'trending_up',
  [WidgetType.DEPARTMENT_HEAD_SUMMARY]: 'wl_icon_people',
  [WidgetType.INTL_MANAGEMENT_SUMMARY]: 'wl_icon_allclients',
  [WidgetType.CAN_MANAGEMENT_SUMMARY]: 'wl_icon_allclients',
  [WidgetType.CAN_AM_SUMMARY]: 'wl_icon_allclients',
  [WidgetType.CAN_PM_SUMMARY]: 'fa-solid fa-circle-notch',
};

/** Get icon for widget type. Falls back to 'widgets' if unknown. */
export function getIconForWidgetType(widgetType: string): string {
  return WIDGET_TYPE_ICONS[widgetType] ?? 'widgets';
}

/** Resolve API widget name to icon (for My Widgets slider). Uses mapping config. */
export function getIconForApiName(apiName: string): string {
  const widgetTypes = Object.values(WidgetType) as string[];
  const resolved = resolveApiNameToWidget(apiName, widgetTypes);
  return resolved ? getIconForWidgetType(resolved.type) : 'widgets';
}
